import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Badge,
  Popover,
  PopoverBody,
} from 'reactstrap';
import { all, spread } from 'axios';
import { clone } from 'lodash';

import { authorizedPyInstance as py } from '~/utils';

import AllergyDetail from './AllergyDetail';
import CurrentlyHospitalizedDetail from './CurrentlyHospitalizedDetail';
import OnLeaveDetail from './OnLeaveDetail';
import Mcp from './Mcp';

const ALLERGIES = 'Allergies';
const CURRENTLY_HOSPITALIZED = 'Currenly Hospitalized';
const AWOL = 'AWOL';
const ON_LEAVE = 'On Leave';
const RESIDENTIAL_CARE = 'Residential Care';
const MCP = 'OhioRISE';

const ALERTS = [{
  title: ALLERGIES,
  detailComponent: AllergyDetail,
  order: 0, // this is the order in which the alerts are presented in the identity demo app
}, {
  title: CURRENTLY_HOSPITALIZED,
  detailComponent: CurrentlyHospitalizedDetail,
  order: 1,
}, {
  title: AWOL,
  detailComponent: OnLeaveDetail, // same presentation as on leave
  order: 2,
}, {
  title: ON_LEAVE,
  detailComponent: OnLeaveDetail,
  order: 3,
}, {
  title: RESIDENTIAL_CARE,
  detailComponent: CurrentlyHospitalizedDetail, // same presentation as currently hospitalized
  order: 4,
}, {
  title: MCP,
  detailComponent: Mcp,
  order: 5,
}];

const PatientAlerts = ({ id: patientUid, restrictedFlag }) => {
  const [alerts, setAlerts] = useState([]);
  const [openAlert, setOpenAlert] = useState();

  const toggleOpenAlert = (alertId) => {
    if (openAlert === alertId) {
      setOpenAlert(undefined);
    } else {
      setOpenAlert(alertId);
    }
  };

  useEffect(() => {
    if (patientUid) {
      all([
        py.get('api/data/public/list_view_cs_tbl_allergy', { params: { patientUid } }),
        py.get('api/data/public/list_view_cs_tbl_currenthospitalizations', { params: { patientUid } }),
        py.get('api/data/public/list_view_cs_tbl_source_child', { params: { patientUid } }),
        py.get('api/data/public/tbl_mcp', { params: { patientUid } }),
      ])
        .then(spread((
          allergies,
          currentHospitalizations,
          sourceChildren,
          mcp,
        ) => ([
          allergies.data.data,
          currentHospitalizations.data.data,
          sourceChildren.data.data,
          mcp.data.data,
        ])))
        .then(spread((
          allergies,
          currentHospitalizations,
          sourceChildren,
          mcps,
        ) => {
          const allergyData = allergies;
          const currentlyHospitalizedData = currentHospitalizations.filter(ch => ch.attributes.resyn === '0');
          const AWOLData = sourceChildren.filter(ch => ch.attributes.leaveType && ch.attributes.leaveType.toLowerCase().includes('awol'));
          const onLeaveData = sourceChildren.filter(ch => ch.attributes.leaveType && !ch.attributes.leaveType.toLowerCase().includes('awol'));
          const residentialCareData = currentHospitalizations.filter(ch => ch.attributes.resyn === '1');
          const mcpData = mcps.filter(ch => ch.attributes.mcpName?.includes('OhioRISE'));

          const newAlerts = clone(ALERTS);

          newAlerts.find(a => a.title === ALLERGIES).data = allergyData;
          newAlerts.find(a => a.title === CURRENTLY_HOSPITALIZED).data = currentlyHospitalizedData;
          newAlerts.find(a => a.title === AWOL).data = AWOLData;
          newAlerts.find(a => a.title === ON_LEAVE).data = onLeaveData;
          newAlerts.find(a => a.title === RESIDENTIAL_CARE).data = residentialCareData;
          newAlerts.find(a => a.title === MCP).data = mcpData;

          setAlerts(newAlerts);
        }));
    }
  }, [patientUid]);

  return alerts.length > 0 || Number(restrictedFlag) === 1
    ? (
      <div className="p-3 mb-3 d-print-none badge-container rounded">
        <h5 className="mb-0">
          {Number(restrictedFlag) === 1 && (
            <Badge
              color="danger"
              pill
            >
              <FontAwesomeIcon icon="exclamation-circle" className="mr-3" />
              Is Restricted
            </Badge>
          )}

          {alerts
            .filter(a => a.data.length)
            .map(a => (
              <Badge
                pill
                key={a.order}
                id={`alert-${a.order}`}
                color="danger"
                className="cursor-pointer"
                onClick={() => toggleOpenAlert(a.order)}
              >
                <FontAwesomeIcon icon="exclamation-circle" className="mr-3" />
                {a.title}

                <Popover
                  target={`alert-${a.order}`}
                  isOpen={a.order === openAlert}
                  toggle={() => toggleOpenAlert(a.order)}
                  placement="bottom"
                >
                  <PopoverBody>
                    <a.detailComponent data={a.data} />
                  </PopoverBody>
                </Popover>
              </Badge>
            ))
          }
        </h5>
      </div>
    ) : null;
};

PatientAlerts.propTypes = {
  id: PropTypes.string,
  restrictedFlag: PropTypes.string,
};

PatientAlerts.defaultProps = {
  id: undefined,
  restrictedFlag: undefined,
};

export default PatientAlerts;
